import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { JSONApiDataObject, ReduxEntity, SellerProfileCustom } from 'Douleutaras/types';
import normalize from 'json-api-normalizer';
import { RootState } from 'Douleutaras/redux/store';
import { sellerProfilesBadgesSlice } from 'Douleutaras/redux/data/seller/sellerProfilesBadgesSlice';
import { sellerProfilesCategoriesSlice } from 'Douleutaras/redux/data/seller/sellerProfilesCategoriesSlice';
import { sellerProfilesPortfolioSlice } from 'Douleutaras/redux/data/seller/sellerProfilesPortfolioSlice';

export const sellerEndpoint = 'api/public/v3/seller/';
export const singleSellerEndpoint = userName => `${sellerEndpoint}${userName}/`;

interface ProfileNormalizedEntityList {
  users: {};
  sellerProfiles: {};
  feedback: {};
  feedbackResponse: {};
  badge: {};
  categories: {};
  sellerPortfolio: {};
  municipality: {};
  meta: {};
  seoSettings: {};
}

const profilesAdapter = createEntityAdapter<JSONApiDataObject<SellerProfileCustom>>({
  selectId: profile => profile.id,
  sortComparer: (a, b) => a.attributes.username.localeCompare(b.attributes.username)
});

// Add custom selectors for username-based selection
export const sellerProfilesSelectors = {
  ...profilesAdapter.getSelectors((state: RootState) => state.sellerProfiles),
  selectByUsername: (state: RootState, username: string) => {
    const profiles = profilesAdapter
      .getSelectors((rootState: RootState) => rootState.sellerProfiles)
      .selectAll(state);

    return profiles.find(profile => profile.attributes.username === username);
  }
};
export const fetchSeller = createAsyncThunk(
  'seller/fetch',
  async ({username, includes, additionalGetParams}:
           {
             username: string,
             includes?: Array<string>,
             additionalGetParams?: {}} = undefined,
         api
  ) => {
    const response = await makeRequest(
      singleSellerEndpoint(username),
      {
        method: 'GET',
        includes,
        additionalGetParams
      }
    )(api);

    if (response.payload?.response?.status === 404) {
      return api.rejectWithValue(response.payload);
    }

    return profileHandleResponse(response, api, sellerEndpoint);
  });

const profileHandleResponse = (response, api, endpoint) => {
  const normalizedProProfiles = normalize(response.data, { endpoint }) as ProfileNormalizedEntityList;
  const { sellerProfilesBadgesAdded } = sellerProfilesBadgesSlice.actions;
  const { sellerProfilesCategoriesAdded } = sellerProfilesCategoriesSlice.actions;
  const { sellerProfilesPortfolioAdded } = sellerProfilesPortfolioSlice.actions;

  if (normalizedProProfiles.badge) {
    api.dispatch(sellerProfilesBadgesAdded(normalizedProProfiles.badge));
  }
  if (normalizedProProfiles.categories) {
    api.dispatch(sellerProfilesCategoriesAdded(normalizedProProfiles.categories));
  }
  if (normalizedProProfiles.sellerPortfolio) {
    api.dispatch(sellerProfilesPortfolioAdded(normalizedProProfiles.sellerPortfolio));
  }
  // Data mutation
  if (normalizedProProfiles.sellerProfiles) {
    const normalizedJobList = normalizedProProfiles.sellerProfiles;
    const calcAverage = (aggregates: SellerProfileCustom['aggregates']): number => {
      let counter = 0;
      let sum = 0;
      Object.keys(aggregates)
        .map(key => {
          if (aggregates[key]) {
            counter++;
            sum += Number(aggregates[key]);
          }
        });

      return sum / counter;
    };
    Object.keys(normalizedJobList)
      .map(key => normalizedJobList[key].attributes.averageRating = calcAverage(normalizedJobList[key].attributes.aggregates));
  }

  if (normalizedProProfiles.seoSettings) {
    // @ts-ignore
    const seoSettings = Object.values(normalizedProProfiles.seoSettings)[0].attributes;

    if (seoSettings) {
      Object.keys(normalizedProProfiles.sellerProfiles)
        .forEach(key => {
        normalizedProProfiles.sellerProfiles[key].attributes.desktopBackgroundImage =
          seoSettings.desktopBackgroundImage || '';

        normalizedProProfiles.sellerProfiles[key].attributes.mobileBackgroundImage =
          seoSettings.mobileBackgroundImage || '';
      });
    }
  }

  return normalizedProProfiles;
};

export const sellerProfilesSlice = createSlice({
  name: 'sellerProfiles',
  initialState: profilesAdapter.getInitialState({
    loading: false,
    error: false,
    meta: {},
    entities: {}
  } as ReduxEntity<JSONApiDataObject<SellerProfileCustom>>),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchSeller.fulfilled, (state, action) => {
      profilesAdapter.upsertMany(state, action.payload.sellerProfiles);
      state.meta = action.payload.meta[sellerEndpoint];
      state.loading = false;
      state.error = false;
    });
    builder.addCase(fetchSeller.pending, (state, action) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(fetchSeller.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
    });
  }
});

export default sellerProfilesSlice.reducer;
